// SPDX-License-Identifier: GPL-3.0-or-later
// sigmashake-pinball frozen contracts (wave A, run-20261026T0044-f274d9).
//
// This file has zero imports and zero runtime dependencies. Every slice reads it.
// Do not change a frozen shape in a feature slice. To change a shape, ask the
// architect, bump CONTRACT_VERSION, and update every consumer in one change.
//
// Naming:
// - In-process APIs (TableSpec, engine, rules) use camelCase. TableSpec item
//   fields keep the upstream table field names where possible (center, radius,
//   startAngle, szMaterial, dragPoints, ...), so the port can map them 1:1.
// - Wire messages (HTTP bodies, WebSocket frames) use snake_case.
// - Distances are table units (the upstream unit: 1 unit is about 0.54 mm).
//   Angles in a TableSpec are degrees. Times are milliseconds unless a name
//   ends in _s or Seconds.

/** Version of these contracts. A shape change bumps this number. */
export const CONTRACT_VERSION = 1 as const;

// ───────────────────────────────────────────────────────────────────────────
// (d) Limits. Every slice reads limits from here. Do not copy a number.
// ───────────────────────────────────────────────────────────────────────────

/** Maximum display name length after sanitizing, in Unicode code points. */
export const DISPLAY_NAME_MAX_LEN = 25;
/** Maximum login length (platform login names are 25 or fewer). */
export const LOGIN_MAX_LEN = 25;
/** Maximum platform user id length. */
export const USER_ID_MAX_LEN = 64;
/** Maximum source event id length. */
export const EVENT_ID_MAX_LEN = 128;
/** Maximum POST /api/grant body size in bytes. Larger bodies get 413. */
export const GRANT_MAX_BODY_BYTES = 4096;
/** Maximum clock skew between the grant timestamp and the Worker clock. */
export const GRANT_SKEW_SECONDS = 300;
/** The Worker keeps a grant event id this long to drop duplicates. */
export const IDEMPOTENCY_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** A turn credit that nobody plays expires after this time. The UI shows the date. */
export const TURN_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
/** A claim code is valid for this time. It is the same as the turn expiry. */
export const CLAIM_CODE_TTL_MS = TURN_EXPIRY_MS;
/** Maximum entries in the featured-turn queue. Extra credits wait in the wallet. */
export const MAX_QUEUE = 50;
/** The head of the queue has this time to start. Then the credit goes back to the wallet. */
export const READY_WINDOW_MS = 120_000;
/** A featured turn ends after this time, with the score at that time. */
export const FEATURED_TURN_MAX_MS = 15 * 60 * 1000;
/** A live turn with no client socket for this time ends, with the score at that time. */
export const LIVE_DISCONNECT_GRACE_MS = 30_000;
/** A turn ticket (from POST /api/turn/start) is valid for this time and one use. */
export const TURN_TICKET_TTL_MS = 60_000;
/** Maximum state frames per second from the featured client. Extra frames are dropped. */
export const STATE_MSG_MAX_PER_SEC = 10;
/** Maximum size of one client WebSocket frame in bytes. Larger frames close the socket. */
export const WS_MSG_MAX_BYTES = 2048;
/**
 * Maximum score gain per second of game time that the Durable Object accepts.
 * The DO clamps a larger gain. The table rules must keep the legal maximum
 * below this value (B3 has a test for this).
 */
export const MAX_SCORE_DELTA_PER_SEC = 250_000;
/** Maximum score that the DO accepts for one turn. */
export const MAX_TURN_SCORE = 999_999_999;
/** Number of queue entries in the overlay snapshot. */
export const OVERLAY_QUEUE_PREVIEW = 5;
/** Maximum overlay event frames per second from the DO. Extra updates are coalesced. */
export const OVERLAY_EVENT_MAX_PER_SEC = 10;
/**
 * Maximum open public overlay sockets. One IP can hold 2 (a third gets 429). At this cap the hub
 * closes the oldest public socket with code 4001. IPv6 addresses count by /64. A socket with the
 * overlay key (see OVERLAY_KEY_PARAM) is not public: it does not count here and nobody evicts it.
 */
export const OVERLAY_WS_MAX_CONNECTIONS = 16;
/** Maximum open overlay sockets that carry the right overlay key. At this cap the oldest of them closes (code 4001). */
export const OVERLAY_RESERVED_MAX_CONNECTIONS = 4;
/**
 * Query parameter that carries the overlay key. The OBS source URL is `/overlay?k=<key>`. The
 * overlay page passes the same value to the overlay socket: `/api/overlay/ws?k=<key>`.
 * The Worker setting PINBALL_OVERLAY_KEY holds the key. A deploy without it has no reserved slot.
 */
export const OVERLAY_KEY_PARAM = "k";
/** Longest overlay key that the Worker reads from a request. A longer value counts as no key. */
export const OVERLAY_KEY_MAX_LEN = 128;
/** Maximum claim attempts per client IP per hour. Extra attempts get 429. */
export const CLAIM_ATTEMPTS_PER_IP_PER_HOUR = 10;
/** Maximum signed grants per minute (all sources). Extra grants get 429; forwarders retry. */
export const GRANT_MAX_PER_MIN = 120;
/** Maximum signed revokes per minute. It is a bucket apart from the grants. Extra revokes get 429. */
export const REVOKE_MAX_PER_MIN = 120;
/**
 * The hub keeps at most this many live tombstones (a revoke for a grant that it does not know yet).
 * Past the cap, a revoke for an unknown key answers 200 with `revoked: 0` and stores nothing.
 */
export const TOMBSTONE_MAX_ROWS = 10_000;
/** Maximum queue join, leave and turn start calls per wallet per minute. */
export const QUEUE_ACTIONS_PER_MIN = 20;
/** Maximum turns in one operator grant. */
export const OPERATOR_MAX_TURNS_PER_GRANT = 10;
/** Balls in one game. */
export const BALLS_PER_GAME = 3;
/** Minimum time between two nudges. A nudge in this time is ignored (no tilt penalty). */
export const NUDGE_COOLDOWN_MS = 750;
/** Claim code alphabet: Crockford base32, no I, L, O, U. */
export const CLAIM_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** Claim code shape: "PB-" then 4 chars, "-", 4 chars (40 bits of entropy). */
export const CLAIM_CODE_PATTERN = /^PB-[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;

// ───────────────────────────────────────────────────────────────────────────
// Turn rule (binding operator decision 3). A donation earns a fixed perk only:
// featured turns. No random reward, no prize, no wager.
// ───────────────────────────────────────────────────────────────────────────

/** Bits for one turn. */
export const BITS_PER_TURN = 100;
/** Maximum turns from one cheer. */
export const MAX_TURNS_PER_CHEER = 3;
/** Maximum turns from one gift event (credited to the gifter). */
export const MAX_TURNS_PER_GIFT = 5;
/** Minimum tip or paid chat amount for one turn, in USD cents. */
export const MIN_USD_CENTS_PER_TURN = 100;

/**
 * The turn rule as public text. The site and the overlay show these lines
 * exactly. Nothing about turns is hidden. THEME.md may change the words, not
 * the numbers.
 */
export const TURN_RULE_LINES: readonly string[] = [
  "Free play is open to everyone, always.",
  `${BITS_PER_TURN} bits = 1 featured turn (up to ${MAX_TURNS_PER_CHEER} per cheer).`,
  "Each sub or resub = 1 featured turn.",
  `Each gifted sub = 1 featured turn for the gifter (up to ${MAX_TURNS_PER_GIFT} per gift).`,
  "A tip or paid chat message of 1 USD or more = 1 featured turn.",
  "A featured turn shows your game live on stream. It gives no other reward.",
];

/** Grant source. */
export type GrantSource = "twitch" | "youtube" | "tip" | "operator";

/** Grant kind. Each source allows only some kinds (see GRANT_KINDS_BY_SOURCE). */
export type GrantKind =
  | "cheer"
  | "sub"
  | "resub"
  | "gift_sub"
  | "superchat"
  | "tip"
  | "operator_grant";

/** Kinds that each source can send. Other pairs get 422 unknown_kind. */
export const GRANT_KINDS_BY_SOURCE: Readonly<Record<GrantSource, readonly GrantKind[]>> = {
  twitch: ["cheer", "sub", "resub", "gift_sub"],
  /** A YouTube membership is a sub; a gifted membership is a gift_sub; a Super Sticker is a superchat. */
  youtube: ["sub", "gift_sub", "superchat"],
  tip: ["tip"],
  operator: ["operator_grant"],
};

/** The inputs of the turn rule. */
export interface TurnRuleInput {
  readonly kind: GrantKind;
  readonly bits?: number;
  readonly giftCount?: number;
  /** Amount in the minor unit of `currency`. */
  readonly amountCents?: number;
  /** ISO 4217 code, lowercase. */
  readonly currency?: string;
  /** Only for operator_grant. */
  readonly turns?: number;
}

/**
 * Returns the number of featured turns for a grant. The Worker always uses
 * this result. A `turns` field from a forwarder is ignored for every kind
 * except operator_grant.
 *
 * Super Chat in a currency other than usd: the platform minimum is about
 * 1 USD, so any amount above 0 earns 1 turn (assumption A-SC1 in DESIGN.md).
 */
export function computeTurns(input: TurnRuleInput): number {
  switch (input.kind) {
    case "cheer":
      return Math.min(MAX_TURNS_PER_CHEER, Math.floor(wholeOrZero(input.bits) / BITS_PER_TURN));
    case "sub":
    case "resub":
      return 1;
    case "gift_sub":
      return Math.min(MAX_TURNS_PER_GIFT, wholeOrZero(input.giftCount));
    case "tip":
      return input.currency === "usd" ? usdTurn(input.amountCents) : 0;
    case "superchat":
      return input.currency === "usd"
        ? usdTurn(input.amountCents)
        : anyAmountTurn(input.amountCents);
    case "operator_grant":
      return Math.min(OPERATOR_MAX_TURNS_PER_GRANT, wholeOrZero(input.turns));
  }
}

/** A finite number above 0, cut down to a whole number. Everything else is 0. */
function wholeOrZero(n: number | undefined): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** One turn for an amount of 1 USD or more. */
function usdTurn(amountCents: number | undefined): number {
  return wholeOrZero(amountCents) >= MIN_USD_CENTS_PER_TURN ? 1 : 0;
}

/** One turn for any amount above 0 (a Super Chat in another currency, assumption A-SC1). */
function anyAmountTurn(amountCents: number | undefined): number {
  return wholeOrZero(amountCents) > 0 ? 1 : 0;
}

// ───────────────────────────────────────────────────────────────────────────
// (a) TableSpec: typed table data. B1 builds physics from it, B2 builds meshes
// from it, B3 writes it, B6 edits it. JSON-safe: no functions, no classes.
// ───────────────────────────────────────────────────────────────────────────

/** A 2D point in table units. */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/** A 3D point in table units. z is height above the playfield. */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** One control point of a wall, rubber, ramp, trigger, or light outline. Upstream: DragPoint. */
export interface DragPointSpec {
  /** Position. For a ramp, z is the height at this point when `calcHeight` is not set. */
  readonly vertex: Vec3;
  /** Smooth curve through this point (Catmull-Rom). Upstream: fSmooth. */
  readonly fSmooth?: boolean;
  /** The segment that starts at this point is a slingshot. Upstream: fSlingshot. */
  readonly fSlingshot?: boolean;
  /** Upstream: fAutoTexture. Default true. */
  readonly fAutoTexture?: boolean;
  /** Upstream: texturecoord. */
  readonly texturecoord?: number;
}

/** Physics overrides that most items accept. Unset fields use the material, then the table. */
export interface PhysicsSpec {
  readonly elasticity?: number;
  readonly elasticityFalloff?: number;
  readonly friction?: number;
  /** Degrees. */
  readonly scatter?: number;
  /** Minimum hit speed that fires a hit event. */
  readonly threshold?: number;
}

/** Fields that every item has. */
export interface ItemBaseSpec {
  /** Unique id in the table. Rules and events use it. Pattern: [A-Za-z][A-Za-z0-9_]{0,31}. */
  readonly name: string;
  /** Name of a wall that this item stands on. Unset: the playfield. Upstream: szSurface. */
  readonly szSurface?: string;
  /** Material name (TableSpec.materials). Upstream: szMaterial. */
  readonly szMaterial?: string;
  /** Default true. */
  readonly isVisible?: boolean;
  /** Free tags for rules (for example "lane:left", "group:targets_a"). */
  readonly tags?: readonly string[];
}

/** Upstream: Material (render + physics part). */
export interface MaterialSpec {
  readonly name: string;
  /** 0xRRGGBB. */
  readonly baseColor: number;
  readonly roughness?: number;
  readonly glossiness?: number;
  readonly clearCoat?: number;
  readonly isMetal?: boolean;
  /** 0..1. */
  readonly opacity?: number;
  readonly isOpacityActive?: boolean;
  /** 0xRRGGBB. */
  readonly emissiveColor?: number;
  readonly emissiveIntensity?: number;
  readonly elasticity?: number;
  readonly elasticityFalloff?: number;
  readonly friction?: number;
  /** Degrees. Upstream: scatterAngle. */
  readonly scatterAngle?: number;
}

/** Upstream: Flipper. */
export interface FlipperSpec extends ItemBaseSpec, PhysicsSpec {
  readonly center: Vec2;
  readonly baseRadius: number;
  readonly endRadius: number;
  /** Flipper length. Upstream: flipperRadiusMax. */
  readonly flipperRadiusMax: number;
  /** Degrees, rest angle. */
  readonly startAngle: number;
  /** Degrees, active angle. */
  readonly endAngle: number;
  readonly height: number;
  readonly mass?: number;
  readonly strength?: number;
  /** Return strength ratio. Upstream: return. */
  readonly return?: number;
  readonly rampUp?: number;
  readonly torqueDamping?: number;
  readonly torqueDampingAngle?: number;
  readonly rubberThickness?: number;
  readonly rubberHeight?: number;
  readonly rubberWidth?: number;
  readonly szRubberMaterial?: string;
  /** Which button moves it. */
  readonly side: "left" | "right";
  readonly isEnabled?: boolean;
}

/** Upstream: Bumper. */
export interface BumperSpec extends ItemBaseSpec {
  readonly center: Vec2;
  readonly radius: number;
  readonly force: number;
  readonly threshold?: number;
  /** Degrees. */
  readonly scatter?: number;
  readonly heightScale?: number;
  /** Degrees. */
  readonly orientation?: number;
  readonly ringSpeed?: number;
  readonly szCapMaterial?: string;
  readonly szRingMaterial?: string;
  readonly szBaseMaterial?: string;
  readonly szSkirtMaterial?: string;
  readonly isCollidable?: boolean;
  /** Decoration id (see DecorationSpec.meshId) to put on the cap. */
  readonly capDecoration?: string;
}

/** Upstream: Surface (a wall). */
export interface WallSpec extends ItemBaseSpec, PhysicsSpec {
  /** Closed outline, 3 or more points, in table coordinates. Either direction is valid: the engine makes the wall solid on the outside. */
  readonly dragPoints: readonly DragPointSpec[];
  readonly heightBottom?: number;
  readonly heightTop: number;
  readonly hitEvent?: boolean;
  readonly isDroppable?: boolean;
  readonly isCollidable?: boolean;
  readonly isBottomSolid?: boolean;
  readonly slingshotForce?: number;
  readonly slingshotThreshold?: number;
  readonly szTopMaterial?: string;
  readonly szSideMaterial?: string;
  readonly szSlingShotMaterial?: string;
  readonly isTopBottomVisible?: boolean;
  readonly isSideVisible?: boolean;
}

/** Upstream: Rubber. */
export interface RubberSpec extends ItemBaseSpec, PhysicsSpec {
  readonly dragPoints: readonly DragPointSpec[];
  readonly height: number;
  /** Upstream default -1: use `height`. */
  readonly hitHeight?: number;
  readonly thickness: number;
  readonly hitEvent?: boolean;
  readonly isCollidable?: boolean;
  /** Degrees. */
  readonly rotX?: number;
  readonly rotY?: number;
  readonly rotZ?: number;
}

/** Upstream: RampType values. Names are ours. */
export type RampType = 0 | 1 | 2 | 3 | 4 | 5; // 0 flat, 1 four-wire, 2 two-wire, 3 three-wire left, 4 three-wire right, 5 one-wire

/** Upstream: Ramp. */
export interface RampSpec extends ItemBaseSpec, PhysicsSpec {
  readonly dragPoints: readonly DragPointSpec[];
  readonly heightBottom: number;
  readonly heightTop: number;
  readonly widthBottom: number;
  readonly widthTop: number;
  readonly rampType?: RampType;
  readonly leftWallHeight?: number;
  readonly rightWallHeight?: number;
  readonly leftWallHeightVisible?: number;
  readonly rightWallHeightVisible?: number;
  readonly wireDiameter?: number;
  readonly wireDistanceX?: number;
  readonly wireDistanceY?: number;
  readonly hitEvent?: boolean;
  readonly isCollidable?: boolean;
}

/** Upstream: KickerType values 0..3 and 6. Values 4 and 5 are not used. */
export type KickerType = 0 | 1 | 2 | 3 | 6; // 0 invisible, 1 hole, 2 cup, 3 simple hole, 6 cup with rim

/** Rule role of a kicker or trigger. The engine handles "drain" itself. */
export type ItemRole =
  | "drain"
  | "lock"
  | "scoop"
  | "outlane"
  | "inlane"
  | "shooter_lane"
  | "rollover"
  | "none";

/** Upstream: Kicker. The port always uses legacy mode (no upstream hit mesh). */
export interface KickerSpec extends ItemBaseSpec {
  readonly center: Vec2;
  readonly radius: number;
  readonly kickerType?: KickerType;
  /** Degrees. */
  readonly orientation?: number;
  readonly hitAccuracy?: number;
  readonly hitHeight?: number;
  /** Degrees. */
  readonly scatter?: number;
  readonly fallThrough?: boolean;
  readonly isEnabled?: boolean;
  readonly role?: ItemRole;
  /** Eject when rules call kick(): angle in degrees, speed in units per physics step. */
  readonly ejectAngle?: number;
  readonly ejectSpeed?: number;
}

/** Upstream: TriggerShape values. */
export type TriggerShape = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 none, 1 wire A, 2 star, 3 wire B, 4 button, 5 wire C, 6 wire D

/** Upstream: Trigger. */
export interface TriggerSpec extends ItemBaseSpec {
  readonly center: Vec2;
  /** Hit outline. Empty: a circle with `radius`. */
  readonly dragPoints?: readonly DragPointSpec[];
  readonly radius?: number;
  /** Degrees. */
  readonly rotation?: number;
  readonly shape?: TriggerShape;
  readonly hitHeight?: number;
  readonly wireThickness?: number;
  readonly isEnabled?: boolean;
  readonly role?: ItemRole;
}

/** Upstream: GateType values. */
export type GateType = 1 | 2 | 3 | 4; // 1 wire, 2 wire rectangle, 3 plate, 4 long plate

/** Upstream: Gate. */
export interface GateSpec extends ItemBaseSpec {
  readonly center: Vec2;
  readonly length: number;
  readonly height: number;
  /** Degrees. */
  readonly rotation: number;
  /** Radians, as upstream. */
  readonly angleMin?: number;
  readonly angleMax?: number;
  readonly damping?: number;
  readonly elasticity?: number;
  readonly friction?: number;
  readonly gravityFactor?: number;
  readonly gateType?: GateType;
  readonly twoWay?: boolean;
  readonly showBracket?: boolean;
  readonly isCollidable?: boolean;
}

/** Upstream: Spinner. */
export interface SpinnerSpec extends ItemBaseSpec {
  readonly center: Vec2;
  readonly length: number;
  readonly height: number;
  /** Degrees. */
  readonly rotation: number;
  readonly damping?: number;
  readonly elasticity?: number;
  /** Degrees. 0 and 0: free spin. */
  readonly angleMin?: number;
  readonly angleMax?: number;
  readonly showBracket?: boolean;
}

/** Upstream: Plunger. One per table. */
export interface PlungerSpec extends ItemBaseSpec {
  readonly center: Vec2;
  readonly width: number;
  readonly height: number;
  readonly zAdjust?: number;
  readonly stroke?: number;
  readonly speedPull?: number;
  readonly speedFire?: number;
  readonly mechStrength?: number;
  /** 0..1. */
  readonly parkPosition?: number;
  readonly scatterVelocity?: number;
  readonly momentumXfer?: number;
  /** The engine fires it at ball start. Default false. */
  readonly autoPlunger?: boolean;
}

/** Upstream: LightStatus values. */
export type LightState = 0 | 1 | 2; // 0 off, 1 on, 2 blinking

/** Upstream: Light (playfield insert or bulb). */
export interface LightSpec extends ItemBaseSpec {
  readonly center: Vec2;
  /** Insert outline. Empty: a circle with `falloff`. */
  readonly dragPoints?: readonly DragPointSpec[];
  readonly falloff?: number;
  readonly falloffPower?: number;
  /** 0xRRGGBB, lit. */
  readonly color: number;
  /** 0xRRGGBB, full brightness tint. */
  readonly color2?: number;
  readonly intensity?: number;
  readonly state?: LightState;
  /** Upstream blink pattern, "1" on, "0" off per step. */
  readonly rgBlinkPattern?: string;
  readonly blinkInterval?: number;
  readonly bulbLight?: boolean;
  readonly showBulbMesh?: boolean;
  readonly meshRadius?: number;
  readonly bulbHaloHeight?: number;
  /** Short label for the insert (for example "LGTM"). Drawn on a canvas texture. */
  readonly label?: string;
}

/** Upstream: TargetType values. */
export type TargetType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
// 1 drop beveled, 2 drop simple, 3 hit round, 4 hit rectangle, 5 hit fat rectangle,
// 6 hit fat square, 7 drop flat simple, 8 hit fat slim, 9 hit slim

/** Upstream: HitTarget. */
export interface TargetSpec extends ItemBaseSpec, PhysicsSpec {
  readonly position: Vec3;
  /** Upstream: vSize. Default 32, 32, 32. */
  readonly vSize?: Vec3;
  /** Degrees. */
  readonly rotZ?: number;
  readonly targetType: TargetType;
  readonly isDropped?: boolean;
  readonly dropSpeed?: number;
  readonly raiseDelay?: number;
  readonly isCollidable?: boolean;
  /** Face label or decoration texture id. */
  readonly label?: string;
}

/** A code-built mesh placed on the table. The client resolves meshId in its decoration registry. */
export interface DecorationSpec extends ItemBaseSpec {
  /** Registry id, for example "char.agent_bot". B3 owns the registry. */
  readonly meshId: string;
  readonly position: Vec3;
  /** Degrees, XYZ order. */
  readonly rotation?: Vec3;
  readonly scale?: Vec3;
  /** Simple collider. Unset: no collision. */
  readonly collider?: {
    readonly kind: "cylinder";
    readonly radius: number;
    readonly height: number;
  };
  /** Animation the client plays when rules send this name. */
  readonly animations?: readonly string[];
}

/** Table-wide data. Upstream: TableData (subset). */
export interface TableInfoSpec {
  readonly name: string;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly glassHeight: number;
  readonly tableHeight?: number;
  /** Playfield slope in degrees. Upstream: angletiltMin and angleTiltMax. */
  readonly angletiltMin: number;
  readonly angleTiltMax: number;
  /** 0..1, picks a slope between min and max. Upstream: globalDifficulty. */
  readonly globalDifficulty: number;
  /** Gravity strength as upstream TableData.gravity (upstream DEFAULT_TABLE_GRAVITY is 0.97). */
  readonly gravity: number;
  readonly friction: number;
  readonly elasticity: number;
  readonly elasticityFalloff: number;
  /** Degrees. */
  readonly scatter: number;
  readonly defaultScatter?: number;
  readonly plungerNormalize?: number;
  readonly plungerFilter?: boolean;
  readonly szPlayfieldMaterial?: string;
  /** Playfield art id in the decoration registry (a canvas texture). */
  readonly playfieldArt?: string;
  readonly ballRadius: number;
  readonly ballMass: number;
}

/** A full table. JSON-safe. */
export interface TableSpec {
  readonly version: typeof CONTRACT_VERSION;
  /** Table id, for example "vibe-01". */
  readonly id: string;
  readonly info: TableInfoSpec;
  readonly materials: readonly MaterialSpec[];
  readonly flippers: readonly FlipperSpec[];
  readonly bumpers: readonly BumperSpec[];
  readonly walls: readonly WallSpec[];
  readonly rubbers: readonly RubberSpec[];
  readonly ramps: readonly RampSpec[];
  readonly kickers: readonly KickerSpec[];
  readonly triggers: readonly TriggerSpec[];
  readonly gates: readonly GateSpec[];
  readonly spinners: readonly SpinnerSpec[];
  readonly plunger: PlungerSpec;
  readonly lights: readonly LightSpec[];
  readonly targets: readonly TargetSpec[];
  readonly decorations: readonly DecorationSpec[];
  /** Where a new ball appears (normally on the plunger). */
  readonly ballStart: Vec3;
}

/** Item kinds in events and render frames. */
export type ItemKind =
  | "flipper"
  | "bumper"
  | "wall"
  | "rubber"
  | "ramp"
  | "kicker"
  | "trigger"
  | "gate"
  | "spinner"
  | "plunger"
  | "light"
  | "target"
  | "decoration";

// ───────────────────────────────────────────────────────────────────────────
// Physics -> rules seam (B1 calls, B3 implements). Keeps B1 and B3 apart.
// ───────────────────────────────────────────────────────────────────────────

/** One physics event. Upstream events: Hit, Unhit, Slingshot, Spin. */
export interface PhysicsEvent {
  readonly type: "hit" | "unhit" | "slingshot" | "spin" | "drain";
  readonly item: string;
  readonly kind: ItemKind;
  readonly ballId: number;
  /** Game time. */
  readonly timeMs: number;
  /** Ball speed at the event, units per physics step. */
  readonly speed: number;
}

/** What rules can do. B1 implements it. Every call is deterministic. */
export interface RulesContext {
  readonly timeMs: number;
  readonly ballNumber: number;
  readonly ballsInPlay: number;
  addScore(points: number, reason: string, item?: string): void;
  startMode(modeId: string): void;
  endMode(modeId: string, completed: boolean, bonus: number): void;
  setLight(name: string, state: LightState): void;
  /** Eject the ball held in a kicker. Uses the kicker eject angle and speed when unset. */
  kick(kicker: string, angleDeg?: number, speed?: number): void;
  /** Add a ball from a kicker or the plunger lane (multiball). */
  addBall(from: string): void;
  /** Start or extend ball save. */
  ballSave(durationMs: number): void;
  /** Raise all drop targets with this tag. */
  resetTargets(tag: string): void;
  /** Ask the client to play a decoration animation. */
  animate(decoration: string, animation: string): void;
  /** Set the score multiplier (1..10). */
  setMultiplier(value: number): void;
  /** End the current ball now (for example, the third tilt). The engine drains every ball in play. */
  endBall(reason: string): void;
}

/** A published mode. The how-to-play card lists every mode. */
export interface ModeInfo {
  readonly id: string;
  readonly label: string;
  /** One STE sentence: how to start it and what it scores. */
  readonly description: string;
}

/** A published score value. Every scoring rule is listed: no hidden values. */
export interface ScoreRule {
  readonly label: string;
  readonly points: number;
}

/** Table rules. B3 implements. Pure logic: no clock, no randomness, no I/O. */
export interface TableRules {
  readonly tableId: string;
  readonly modes: readonly ModeInfo[];
  readonly scoreTable: readonly ScoreRule[];
  /** Highest legal score gain in one second. Must be below MAX_SCORE_DELTA_PER_SEC. */
  readonly maxScorePerSecond: number;
  onGameStart(ctx: RulesContext): void;
  onBallStart(ctx: RulesContext): void;
  onEvent(ctx: RulesContext, ev: PhysicsEvent): void;
  onTick(ctx: RulesContext, dtMs: number): void;
  /** Called when the last ball in play drains and ball save is off. */
  onBallEnd(ctx: RulesContext): { readonly bonus: number };
}

// ───────────────────────────────────────────────────────────────────────────
// (b) Engine API: the client app calls it. B1 implements createEngine.
// ───────────────────────────────────────────────────────────────────────────

/** Player inputs. */
export type InputAction =
  | "left_flipper"
  | "right_flipper"
  | "plunger"
  | "nudge_left"
  | "nudge_right"
  | "nudge_up"
  | "start";

/** One input change. */
export interface InputEvent {
  readonly action: InputAction;
  /** true on press, false on release. Plunger: press pulls, release fires. */
  readonly pressed: boolean;
  /** Analog plunger pull 0..1 (touch). Unset: the pull time sets the strength. */
  readonly value?: number;
}

/** Game phase. */
export type GamePhase = "attract" | "ball_ready" | "playing" | "ball_end" | "game_over";

/** Engine output events. */
export type EngineEvent =
  | { readonly type: "game_start"; readonly ballsPerGame: number }
  | { readonly type: "ball_start"; readonly ballNumber: number }
  | {
      readonly type: "score";
      readonly points: number;
      readonly total: number;
      readonly reason: string;
      readonly item?: string;
    }
  | {
      readonly type: "ball_lost";
      readonly ballNumber: number;
      readonly ballsLeft: number;
      readonly bonus: number;
    }
  | { readonly type: "ball_saved"; readonly ballNumber: number }
  | { readonly type: "mode_start"; readonly modeId: string; readonly label: string }
  | {
      readonly type: "mode_end";
      readonly modeId: string;
      readonly completed: boolean;
      readonly bonus: number;
    }
  | { readonly type: "multiball"; readonly ballsInPlay: number; readonly active: boolean }
  | { readonly type: "light"; readonly name: string; readonly state: LightState }
  | { readonly type: "animate"; readonly decoration: string; readonly animation: string }
  | {
      readonly type: "game_over";
      readonly finalScore: number;
      readonly ballsPlayed: number;
      readonly durationMs: number;
    };

/** Snapshot of the game for the HUD and the featured stream. */
export interface GameStatus {
  readonly phase: GamePhase;
  readonly score: number;
  /** 1-based. 0 before the first ball. */
  readonly ballNumber: number;
  readonly ballsPerGame: number;
  readonly ballsInPlay: number;
  readonly multiplier: number;
  readonly activeModes: readonly string[];
  readonly ballSaveMsLeft: number;
  /** Game time since game start. */
  readonly elapsedMs: number;
}

/** Ball state for the renderer. */
export interface BallFrame {
  readonly id: number;
  readonly pos: Vec3;
  /** Orientation as a row-major 3x3 matrix (9 numbers). */
  readonly orientation: readonly number[];
}

/** Moving-part state for the renderer. Only changed items are in a frame. */
export interface ItemFrame {
  readonly name: string;
  readonly kind: ItemKind;
  /** Flipper, gate, spinner: angle in radians. */
  readonly angle?: number;
  /** Bumper ring, drop target: z offset in units. Plunger: pull distance from the forward limit in units (0 = fully forward). */
  readonly offset?: number;
  /** Light: 0..1. */
  readonly intensity?: number;
  readonly visible?: boolean;
}

/** One rendered frame. */
export interface RenderFrame {
  readonly timeMs: number;
  readonly balls: readonly BallFrame[];
  readonly items: readonly ItemFrame[];
}

/** Engine options. */
export interface EngineOptions {
  /** Seed for every random value (scatter). Same seed + same inputs = same game. */
  readonly seed: number;
  readonly ballsPerGame?: number;
  /** Physics step. Upstream: 1 ms (PHYSICS_STEPTIME). */
  readonly physicsStepMs?: number;
}

/** The engine. One instance plays one game at a time. */
export interface PinballEngine {
  readonly table: TableSpec;
  /** Start a new game. Same as the "start" input in attract or game_over. */
  start(): void;
  /** Queue one input. It takes effect at the next physics step. */
  input(ev: InputEvent): void;
  /** Advance by dtMs of real time (capped at 100 ms) and return the frame. */
  step(dtMs: number): RenderFrame;
  status(): GameStatus;
  /** Subscribe to events. Returns the unsubscribe function. */
  on(listener: (ev: EngineEvent) => void): () => void;
  dispose(): void;
}

/** B1 exports a function of this type as `createEngine` from src/engine/index.ts. */
export type CreateEngine = (
  table: TableSpec,
  rules: TableRules,
  opts: EngineOptions,
) => PinballEngine;

/**
 * Renderer seam. B2 exports `createRenderer` from src/render/index.ts. TCanvas is
 * HTMLCanvasElement and TObject is the three.js Object3D in the client build; the
 * generics keep this file free of DOM and three types.
 */
export interface TableRenderer {
  /** Size in CSS pixels and the device pixel ratio. */
  resize(width: number, height: number, pixelRatio: number): void;
  /** Apply one engine frame and draw. */
  render(frame: RenderFrame): void;
  /** Apply an engine event that changes visuals (light, animate). Other events do nothing. */
  applyEvent(ev: EngineEvent): void;
  dispose(): void;
}

/** B2 exports a function of this type as `createRenderer`. */
export type CreateRenderer<TCanvas, TObject> = (
  canvas: TCanvas,
  table: TableSpec,
  decorations: ReadonlyMap<string, DecorationFactory<TObject>>,
) => TableRenderer;

/** B3 exports decoration factories with this shape. TObject is the three.js Object3D. */
export interface DecorationFactory<TObject> {
  readonly id: string;
  build(): TObject;
  /** Play an animation by name. Unknown names do nothing. */
  animate?(obj: TObject, animation: string, timeMs: number): void;
}

// ───────────────────────────────────────────────────────────────────────────
// (c) Worker protocol: HTTP + WebSocket. B4 implements. B5, B7, B8 call.
// ───────────────────────────────────────────────────────────────────────────

/** HTTP header names for a signed grant. */
export const GRANT_TIMESTAMP_HEADER = "X-Pinball-Timestamp";
export const GRANT_SIGNATURE_HEADER = "X-Pinball-Signature";
/**
 * Signature: lowercase hex HMAC-SHA256 with key PINBALL_GRANT_SECRET over the
 * UTF-8 bytes of `${timestamp}.${rawBody}`. timestamp: unix seconds, decimal.
 * The Worker compares in constant time and rejects |now - timestamp| > GRANT_SKEW_SECONDS.
 */
export function grantSigningInput(timestamp: string, rawBody: string): string {
  return `${timestamp}.${rawBody}`;
}

/**
 * Signing input of POST /api/grant/revoke (decision D16): the literal prefix `revoke.` and then
 * the grant form, `revoke.${timestamp}.${rawBody}`. The headers, the HMAC, the key and the skew
 * are the same as for a grant. The prefix keeps the two routes apart: a signature of a grant
 * fails on the revoke route, and a signature of a revoke fails on the grant route.
 */
export function revokeSigningInput(timestamp: string, rawBody: string): string {
  return `revoke.${grantSigningInput(timestamp, rawBody)}`;
}

/** Donor identity on a grant. */
export interface GrantDonor {
  readonly platform: "twitch" | "youtube" | "tip";
  /** Platform user id. Twitch: the numeric user id. Unset for a tip without an account. */
  readonly user_id?: string;
  readonly login?: string;
  readonly display_name: string;
}

/** POST /api/grant body. */
export interface GrantRequest {
  readonly source: GrantSource;
  readonly kind: GrantKind;
  /** Source event id (Twitch message id, ledger id, ...). Idempotency key with `source`. */
  readonly event_id: string;
  /** null when the donor is anonymous. Then `anonymous` is true. */
  readonly donor: GrantDonor | null;
  readonly anonymous: boolean;
  readonly bits?: number;
  readonly gift_count?: number;
  readonly amount_cents?: number;
  readonly currency?: string;
  /** Required for operator_grant. Ignored for the other kinds (computeTurns decides). */
  readonly turns?: number;
}

/** Where the turns went. */
export type GrantCredit =
  | { readonly kind: "twitch"; readonly user_id: string }
  /** The code is in this response only. The forwarder gives it to the donor; never show it on stream. */
  | { readonly kind: "claim_code"; readonly claim_code: string; readonly expires_at: string }
  | { readonly kind: "none" };

/** 200 response of POST /api/grant. A duplicate event id returns the first result with duplicate true. */
export interface GrantResponse {
  readonly ok: true;
  readonly duplicate: boolean;
  readonly grant_id: string;
  readonly turns: number;
  readonly credit: GrantCredit;
}

/** Why a forwarder or the operator takes a grant back. */
export type GrantRevokeReason = "refund" | "dispute" | "operator";

/** The values that GrantRevokeRequest accepts for `reason`. */
export const GRANT_REVOKE_REASONS: readonly GrantRevokeReason[] = ["refund", "dispute", "operator"];

/**
 * POST /api/grant/revoke body (decision D13). The request has the same signature headers, key and
 * skew (300 s) as POST /api/grant. Its signing input has the prefix `revoke.`: see
 * revokeSigningInput (decision D16). It takes back
 * the turns of one grant that nobody has played yet:
 * - An unclaimed claim code of the grant is deleted.
 * - Turns of the grant that are still in a wallet, or waiting in the queue (queued or ready),
 *   are removed.
 * - A turn that is live, or that ended already, stays as it was.
 * The call is idempotent. A repeat, or a grant that the Worker does not know, answers 200 with
 * `revoked: 0`.
 */
export interface GrantRevokeRequest {
  readonly source: GrantSource;
  /** The event id of the grant to take back (the same value as GrantRequest.event_id). */
  readonly event_id: string;
  readonly reason: GrantRevokeReason;
}

/** 200 response of POST /api/grant/revoke. */
export interface GrantRevokeResponse {
  readonly ok: true;
  /** Number of turns that the call removed. 0 for a repeat or an unknown grant. */
  readonly revoked: number;
}

/** Error body for every 4xx and 5xx. Forwarders retry 429 and 5xx; 400, 401, 409, 413, 422 are final. */
export interface ApiError {
  readonly ok: false;
  readonly error:
    | "bad_request"
    | "bad_signature"
    | "stale_timestamp"
    | "too_large"
    | "unknown_kind"
    | "rate_limited"
    | "unauthorized"
    | "not_found"
    | "conflict"
    | "queue_full"
    | "expired"
    | "internal";
  readonly message: string;
}

/** Featured-turn entry state. */
export type TurnState = "queued" | "ready" | "live" | "done" | "returned" | "expired";

/** One queue entry as the public sees it (display name only). */
export interface QueueEntryPublic {
  readonly entry_id: string;
  readonly display_name: string;
  readonly state: TurnState;
  /** 0 = the live turn (playing now). 1..n = waiting; 1 is next. */
  readonly position: number;
}

/** GET /api/queue response (public). */
export interface QueueSnapshot {
  readonly paused: boolean;
  readonly length: number;
  readonly entries: readonly QueueEntryPublic[];
}

/** GET /api/me response (needs the session cookie). */
export interface MeResponse {
  readonly ok: true;
  readonly signed_in: boolean;
  readonly display_name: string | null;
  readonly turns_available: number;
  /** ISO time when the oldest credit expires. */
  readonly next_expiry: string | null;
  readonly entry: QueueEntryPublic | null;
}

/** POST /api/claim body. Binds a claim code to the session wallet. */
export interface ClaimRequest {
  readonly claim_code: string;
  /** Display name for an anonymous wallet. Sanitized; the grant name is used when unset. */
  readonly display_name?: string;
}

/** POST /api/claim 200 response. */
export interface ClaimResponse {
  readonly ok: true;
  readonly turns_added: number;
  readonly turns_available: number;
}

/** POST /api/queue/join and /api/queue/leave 200 response. */
export interface QueueActionResponse {
  readonly ok: true;
  readonly entry: QueueEntryPublic | null;
  readonly turns_available: number;
}

/** POST /api/turn/start 200 response. Only the owner of the "ready" entry gets one. */
export interface TurnStartResponse {
  readonly ok: true;
  readonly turn_id: string;
  /** Single use. Send it in the first WebSocket frame, not in the URL. */
  readonly ticket: string;
  readonly expires_at: string;
}

/** Featured status on the wire (snake_case GameStatus subset). */
export interface FeaturedStatus {
  readonly phase: GamePhase;
  readonly score: number;
  readonly ball_number: number;
  readonly balls_per_game: number;
  readonly balls_in_play: number;
  readonly multiplier: number;
  readonly active_modes: readonly string[];
}

/** Client -> DO frames on WS /api/featured/ws. The first frame must be "auth". */
export type FeaturedClientMessage =
  | { readonly type: "auth"; readonly ticket: string }
  | {
      readonly type: "state";
      readonly seq: number;
      readonly game_ms: number;
      readonly status: FeaturedStatus;
    }
  | {
      readonly type: "event";
      readonly seq: number;
      readonly game_ms: number;
      readonly event: FeaturedEventWire;
    }
  | {
      readonly type: "game_over";
      readonly seq: number;
      readonly game_ms: number;
      readonly final_score: number;
    }
  | { readonly type: "ping" };

/** Engine events that the overlay shows. Other events stay local. */
export type FeaturedEventWire =
  | { readonly type: "mode_start"; readonly mode_id: string; readonly label: string }
  | { readonly type: "mode_end"; readonly mode_id: string; readonly completed: boolean }
  | { readonly type: "multiball"; readonly balls_in_play: number; readonly active: boolean }
  | { readonly type: "ball_lost"; readonly ball_number: number }
  | { readonly type: "ball_saved"; readonly ball_number: number };

/** DO -> featured client frames. */
export type FeaturedServerMessage =
  | { readonly type: "ready"; readonly turn_id: string; readonly ends_at: string }
  /** The DO clamped the score. accepted_score is the value it keeps. */
  | {
      readonly type: "ack";
      readonly seq: number;
      readonly accepted_score: number;
      readonly clamped: boolean;
    }
  | {
      readonly type: "end";
      readonly reason: "game_over" | "time_limit" | "operator" | "disconnect";
      readonly final_score: number;
    }
  | { readonly type: "error"; readonly error: ApiError["error"]; readonly message: string }
  | { readonly type: "pong" };

/** The how-to-play card on the overlay and the site. */
export interface HowToPlayCard {
  readonly title: string;
  readonly controls: readonly string[];
  readonly turn_rule: readonly string[];
  readonly modes: readonly ModeInfo[];
}

/** The featured turn as the overlay sees it. */
export interface FeaturedTurnPublic {
  readonly turn_id: string;
  readonly display_name: string;
  readonly state: TurnState;
  readonly status: FeaturedStatus | null;
  readonly started_at: string | null;
  readonly ends_at: string | null;
}

/** Full overlay state. The DO sends it on connect and after a resync. */
export interface OverlaySnapshot {
  readonly featured: FeaturedTurnPublic | null;
  /** First OVERLAY_QUEUE_PREVIEW entries. */
  readonly queue: readonly QueueEntryPublic[];
  readonly queue_length: number;
  readonly howto: HowToPlayCard;
  /** Overlay shows nothing while hidden is true (operator control). */
  readonly hidden: boolean;
}

/** DO -> /overlay frames on WS /api/overlay/ws (read-only; the overlay sends nothing but ping). */
export type OverlayMessage =
  | { readonly type: "snapshot"; readonly seq: number; readonly snapshot: OverlaySnapshot }
  | { readonly type: "featured_start"; readonly seq: number; readonly turn: FeaturedTurnPublic }
  | {
      readonly type: "featured_update";
      readonly seq: number;
      readonly turn_id: string;
      readonly status: FeaturedStatus;
    }
  | {
      readonly type: "featured_event";
      readonly seq: number;
      readonly turn_id: string;
      readonly event: FeaturedEventWire;
    }
  | {
      readonly type: "featured_end";
      readonly seq: number;
      readonly turn_id: string;
      readonly final_score: number;
      readonly reason: "game_over" | "time_limit" | "operator" | "disconnect";
    }
  | { readonly type: "next_up"; readonly seq: number; readonly entry: QueueEntryPublic }
  | {
      readonly type: "queue";
      readonly seq: number;
      readonly queue: readonly QueueEntryPublic[];
      readonly queue_length: number;
    }
  /** Thank-you line. Display name and turn count only; never a claim code or an amount. */
  | {
      readonly type: "grant";
      readonly seq: number;
      readonly display_name: string;
      readonly turns: number;
    }
  | { readonly type: "pong" };

/** Operator actions on POST /api/operator/action (Bearer PINBALL_OPERATOR_TOKEN). */
export type OperatorAction =
  | { readonly action: "pause_queue" }
  | { readonly action: "resume_queue" }
  | { readonly action: "skip_entry"; readonly entry_id: string }
  | { readonly action: "end_turn"; readonly turn_id: string }
  | { readonly action: "set_overlay_hidden"; readonly hidden: boolean };

/**
 * GET /api/operator/claims response (Bearer PINBALL_OPERATOR_TOKEN). Lists claim
 * codes that nobody has claimed, so the operator can give a code to a donor in
 * private. The overlay and the public API never show a code.
 */
export interface OperatorClaimsResponse {
  readonly ok: true;
  readonly claims: readonly {
    readonly claim_code: string;
    readonly display_name: string;
    readonly source: GrantSource;
    readonly turns: number;
    readonly expires_at: string;
  }[];
}

/** HTTP routes. B4 serves exactly these. */
export const ROUTES = {
  grant: "/api/grant",
  grantRevoke: "/api/grant/revoke",
  queue: "/api/queue",
  me: "/api/me",
  claim: "/api/claim",
  queueJoin: "/api/queue/join",
  queueLeave: "/api/queue/leave",
  turnStart: "/api/turn/start",
  featuredWs: "/api/featured/ws",
  overlayWs: "/api/overlay/ws",
  operatorAction: "/api/operator/action",
  operatorClaims: "/api/operator/claims",
  twitchLogin: "/auth/twitch/login",
  twitchCallback: "/auth/twitch/callback",
  logout: "/auth/logout",
  overlayPage: "/overlay",
  source: "/source",
  healthz: "/healthz",
  live: "/api/live",
} as const;

/** GET /healthz body. */
export interface HealthResponse {
  readonly ok: true;
  readonly service: "sigmashake-pinball";
  readonly version: string;
  /** Git sha of the source in the /source tarball. */
  readonly source_sha: string;
  readonly contract_version: typeof CONTRACT_VERSION;
}

/**
 * GET /api/live body. `live` is true when the Twitch channel of the operator streams now. It is
 * false when the channel is offline and also when the Worker cannot tell (fail closed).
 */
export interface LiveResponse {
  readonly live: boolean;
  /** ISO time of the check that gave `live`. A cached answer keeps the time of its check. */
  readonly checkedAt: string;
}

/**
 * Sanitize text from a user or a client frame: NFC, remove control, format and bidi characters,
 * collapse whitespace, trim, cut to `max` Unicode code points. The result can be empty.
 * Every surface still renders such text with textContent only.
 */
export function sanitizeText(raw: string, max: number): string {
  const cleaned = raw
    .normalize("NFC")
    .replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/gu, "")
    .replace(/\s+/gu, " ")
    .trim();
  return Array.from(cleaned).slice(0, max).join("");
}

/** Sanitize a display name (see sanitizeText), cut to DISPLAY_NAME_MAX_LEN. Empty result: "player". */
export function sanitizeDisplayName(raw: string): string {
  const cut = sanitizeText(raw, DISPLAY_NAME_MAX_LEN);
  return cut.length > 0 ? cut : "player";
}
